import produce, { Immutable } from 'immer'
import { TOverviewView, TRoutine, TRoutineItem } from './types'

type TProgressionStatus = { status?: TOverviewView['status'] }

// Mirrors getRoutineProgressionResults: in routine order, the first unfinished routine is Current and
// the unfinished ones after it are Up next (and locked). Complete and empty routines keep their status.
const relabelProgression = (routines: TProgressionStatus[]) => {
    let hasCurrent = false
    for (const routine of routines) {
        if (routine.status !== 'current' && routine.status !== 'up_next') continue
        routine.status = hasCurrent ? 'up_next' : 'current'
        hasCurrent = true
    }
}

// Daily Overview card order is the routine order, so a moved card re-labels Current and Up next.
export const applyRoutineProgressionToOverview = (views: Immutable<TOverviewView[]>) =>
    produce(views, (draft) => {
        relabelProgression(draft.filter((view) => view.type === 'routine'))
    }) as TOverviewView[]

// Puts the routines list in card order (routines without a card keep their relative order after the
// shown ones) and re-labels progression the same way the server does.
export const applyOverviewOrderToRoutines = (routines: Immutable<TRoutine[]>, views: Immutable<TOverviewView[]>) => {
    const cardPositions = new Map<string, number>()
    views.forEach((view) => {
        if (view.type === 'routine' && view.routine_id && !cardPositions.has(view.routine_id)) {
            cardPositions.set(view.routine_id, cardPositions.size)
        }
    })
    const position = (routine: Immutable<TRoutine>) => cardPositions.get(routine.id) ?? Number.MAX_SAFE_INTEGER
    return produce(routines, (draft) => {
        // Array.prototype.sort is stable, so routines without a card keep their relative order.
        draft.sort((a, b) => position(a) - position(b))
        relabelProgression(draft)
    }) as TRoutine[]
}

export const findRoutineByRouteId = (routines: TRoutine[], routeId?: string, resolvedRouteId?: string) => {
    if (!routeId) return undefined
    return routines.find(
        (routine) =>
            routine.id === routeId ||
            routine.optimisticId === routeId ||
            (!!resolvedRouteId && routine.id === resolvedRouteId)
    )
}

export const applyRoutineItemCompletion = (routines: TRoutine[], itemId: string, isCompleted: boolean) =>
    produce(routines, (draft) => {
        const targetRoutine = draft.find((routine) => routine.items.some((item) => item.id === itemId))
        const targetItem = targetRoutine?.items.find((item) => item.id === itemId)
        if (!targetRoutine || !targetItem || targetItem.is_completed === isCompleted) return

        targetItem.is_completed = isCompleted
        targetRoutine.remaining_count = targetRoutine.items.filter((item) => !item.is_completed).length
    })

export const applyRoutineCompletionToOverview = (
    views: TOverviewView[],
    itemId: string,
    isCompleted: boolean,
    routines?: TRoutine[]
) =>
    produce(views, (draft) => {
        const routineById = new Map(routines?.map((routine) => [routine.id, routine]))
        for (const view of draft) {
            if (view.type !== 'routine') continue
            const routine = view.routine_id ? routineById.get(view.routine_id) : undefined
            if (routine?.items.some((item) => item.id === itemId)) {
                view.view_items = routine.items
                view.remaining_count = routine.remaining_count
                continue
            }

            const item = (view.view_items as TRoutineItem[]).find((candidate) => candidate.id === itemId)
            if (!item || item.is_completed === isCompleted) continue
            item.is_completed = isCompleted
            const items = view.view_items as TRoutineItem[]
            view.remaining_count = items.filter((candidate) => !candidate.is_completed).length
        }
    })

// Mirrors RoutineItemCreate: a new step goes last, and it counts as done when today's routine is already complete.
export const appendOptimisticRoutineItem = (
    routines: TRoutine[],
    routineId: string,
    optimisticId: string,
    title: string
) =>
    produce(routines, (draft) => {
        const routine = draft.find((candidate) => candidate.id === routineId)
        if (!routine) return
        const isCompleted = routine.status === 'complete'
        routine.items.push({
            id: optimisticId,
            optimisticId,
            title,
            id_ordering: routine.items.length + 1,
            is_completed: isCompleted,
        })
        if (!isCompleted) routine.remaining_count += 1
    })

export const resolveOptimisticRoutineItem = (routines: TRoutine[], optimisticId: string, id: string) =>
    produce(routines, (draft) => {
        for (const routine of draft) {
            const item = routine.items.find((candidate) => candidate.optimisticId === optimisticId)
            if (!item) continue
            item.id = id
            item.optimisticId = undefined
        }
    })

// Removes only this step rather than restoring a snapshot, so steps added after it in the same burst survive.
export const removeOptimisticRoutineItem = (routines: TRoutine[], optimisticId: string) =>
    produce(routines, (draft) => {
        for (const routine of draft) {
            const index = routine.items.findIndex((candidate) => candidate.optimisticId === optimisticId)
            if (index === -1) continue
            routine.items.splice(index, 1)
            routine.remaining_count = routine.items.filter((item) => !item.is_completed).length
        }
    })
